import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  inMemoryPersistence,
  setPersistence,
  onAuthStateChanged,
} from "firebase/auth";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";

// Initialize emulator evaluation
declare global {
  interface Window {
    emulatorsEvaluated: boolean;
    emulatorsEvaluatedListeners: (() => void)[];
    onEmulatorsEvaluated: (listener: () => void) => void;
    firebaseInitialized: boolean;
  }
}

window.emulatorsEvaluated = false;
window.emulatorsEvaluatedListeners = [];
window.firebaseInitialized = false;

window.onEmulatorsEvaluated = (listener: () => void) => {
  if (window.emulatorsEvaluated) {
    listener();
  } else {
    window.emulatorsEvaluatedListeners.push(listener);
  }
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase with error handling
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error("Error initializing Firebase:", error);
  throw error;
}

// Initialize services with error handling
export const auth = getAuth(app);
export const storage = getStorage(app);
export const db = getFirestore(app);

// Connect to emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true") {
  console.log("🎮 Initializing Firebase emulators...");

  // Use in-memory persistence for the emulator
  setPersistence(auth, inMemoryPersistence).catch(console.error);

  try {
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectStorageEmulator(storage, "localhost", 9199);
    connectFirestoreEmulator(db, "localhost", 8080);

    // Mark emulators as evaluated
    window.emulatorsEvaluated = true;
    window.emulatorsEvaluatedListeners.forEach((listener) => listener());
    window.emulatorsEvaluatedListeners = [];
  } catch (error) {
    console.error("Error connecting to Firebase emulators:", error);
    throw error;
  }
}

// Initialize auth state listener
onAuthStateChanged(auth, (user) => {
  if (user) {
    console.log("Auth state changed: User is signed in");
  } else {
    console.log("Auth state changed: User is signed out");
  }
});

// Mark Firebase as initialized
window.firebaseInitialized = true;

export default app;
